import type { ComprehensiveScenario } from "@/types/scenarios";

/**
 * Derives permission booleans from a scenario's `currentUserPermission` field.
 *
 * Permission hierarchy:  owner > edit > comment > view
 *
 * Editing is only permitted in 'draft' or 'under-review' states.
 * All other statuses (pending-approval, committed, active, parked, archived)
 * are locked to view-only for everyone — including the owner — to preserve
 * audit integrity and approval controls.
 */

const EDITABLE_STATUSES = new Set(["draft", "under-review"]);

export function useScenarioPermission(
  scenario: ComprehensiveScenario | undefined,
) {
  const permission = scenario?.currentUserPermission;
  const statusAllowsEdit = scenario
    ? EDITABLE_STATUSES.has(scenario.status)
    : false;

  return {
    permission,
    isOwner: permission === "owner",
    canEdit:
      (permission === "owner" || permission === "edit") && statusAllowsEdit,
    canComment:
      permission === "owner" ||
      permission === "edit" ||
      permission === "comment",
    canView: permission != null,
  };
}
